import React, { useEffect, useState } from 'react';

import { useEditUserMutation } from 'src/redux/services/centralApi';
import { Button, Checkbox, Form, Input, Modal, Select, Tooltip } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { User } from 'src/interfaces/entities/user';

type IEditUserForm = Pick<User, 'email' | 'role' | 'phone_number' | 'notify_email' | 'enabled'>;

export default function EditUser(
	{
		initialUser,
	}: { initialUser: User }
) {
	const [ open, setOpen, ] = useState<boolean>(false);
	const [ editUser, editUserResult, ] = useEditUserMutation();

	const [ editUserForm, ] = Form.useForm<IEditUserForm>();

	const onFinishEditCron = (data: IEditUserForm) => {
		editUser({
			id: initialUser.id,
			user: {
				email: data.email,
				role: data.role,
				phone_number: phoneMaskToRaw(data.phone_number),
				notify_email: data.notify_email,
				enabled: data.enabled,
			},
		})
			.unwrap()
			.then(() => {
				toast.success('Cron editado com sucesso!');
			})
			.catch(() => {
				toast.error('Erro ao editar cron!');
			})
			.finally(() => {
				setOpen(false);
			});
	};

	useEffect(() => {
		if (open) {
			const maskedValue = phoneMask(initialUser.phone_number);
			editUserForm.setFieldsValue({
				...initialUser,
				phone_number: maskedValue,
			});
		}
	}, [ initialUser, editUserForm, open, ]);

	const phoneMask = (value: string) => {
		let phoneNumber = value?.replace(/\D/g, '');

		if (phoneNumber.length > 13) {
			phoneNumber = phoneNumber.substring(0, 13);
		}

		phoneNumber = phoneNumber
			.replace(/^(\d{2})(\d{2})(\d)/, '$1 ($2) $3')
			.replace(/(\d)(\d{4})$/, '$1-$2');

		return phoneNumber;
	};

	const phoneMaskToRaw = (value: string) => {
		const rawPhone = value.replace(/\D/g, '');
		return rawPhone;
	};

	const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		const maskedValue = phoneMask(value);
		editUserForm.setFieldsValue({ phone_number: maskedValue, });
		editUserForm.validateFields();
	};

	return <>
		<Tooltip title={'Editar usuário'}>
			<Button
				type="text"
				icon={<EditOutlined />}
				onClick={() => setOpen(true)}
			/>
		</Tooltip>
		<Modal
			centered
			title={'Editar Usuário'}
			open={open}
			onCancel={() => {
				setOpen(false);
				toast.info('Operação cancelada!', { autoClose: 1000, });
			}}
			destroyOnHidden
			confirmLoading={editUserResult.isLoading}
			okText="Salvar"
			onOk={() => editUserForm.submit()}
		>
			<Form
				style={{ marginTop: 20, }}
				onFinish={onFinishEditCron}
				layout="vertical"
				form={editUserForm}
			>
				<Form.Item
					required
					hasFeedback
					label="E-mail"
					name="email"
					initialValue={initialUser.email}
					rules={[
						{ type: 'email', },
						{
							required: true,
							message: 'Campo obrigatório.',
						},
					]}
				>
					<Input
						placeholder="Insira o e-mail do usuário"
					/>
				</Form.Item>
				<Form.Item
					required
					hasFeedback
					label="Telefone"
					name="phone_number"
					initialValue={initialUser.phone_number}
					rules={[
						{
							required: true,
							message: 'Campo obrigatório.',
						},
						{
							validator: (_, value) => {
								if (value.match(/^55 \(\d{2}\) \d{5}-\d{4}$/)) {
									return Promise.resolve();
								}
								return Promise.reject('Insira um número válido.');
							},
							message: 'Insira um número válido.',
						},
					]}
				>
					<Input placeholder="55 (99) 98888-8888" onChange={handlePhoneChange} />
				</Form.Item>
				<Form.Item
					required
					hasFeedback
					label="Função"
					name="role"
					initialValue={initialUser.role}
					rules={[
						{
							required: true,
							message: 'Campo obrigatório.',
						},
					]}
				>
					<Select
						placeholder={'Função'}
						options={[
							{ label: 'Usuário', value: 'user', },
							{ label: 'Administrador', value: 'admin', },
						]}
					/>
				</Form.Item>
				<Form.Item
					required
					hasFeedback
					label="Usuário ativo"
					name="enabled"
					initialValue={initialUser.enabled}
					rules={[
						{
							required: true,
							message: 'Campo obrigatório.',
						},
					]}
					valuePropName='checked'
				>
					<Checkbox />
				</Form.Item>
			</Form>
		</Modal>
	</>;
}